import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Workflow, WorkflowCondition, WorkflowStep, ConditionOperator } from '../../../services/WorkflowService';
import { workflowQueriesApi } from '../../../services/WorkflowService/workflowQueries';
import { workflowMutationsApi } from '../../../services/WorkflowService/workflowMutations';
import { createDefaultWorkflow } from '../../../services/WorkflowService';
import * as wf from './workflowTransforms';

// ── State Shape ────────────────────────────────────────────────────

export interface WorkflowState {
  currentWorkflow: Workflow | null;
  selectedWorkflowId: string | null;
  isEdited: boolean;
  editedFields: string[];
}

const initialState: WorkflowState = {
  currentWorkflow: null,
  selectedWorkflowId: null,
  isEdited: false,
  editedFields: [],
};

// ── Helpers ────────────────────────────────────────────────────────

const addEditedField = (state: WorkflowState, field: string) => {
  if (!state.editedFields.includes(field)) state.editedFields.push(field);
  state.isEdited = true;
};

/** Eliminates repeated null-checks + dirty tracking in every reducer. */
const withWorkflow = (
  state: WorkflowState,
  field: string,
  fn: (w: Workflow) => Workflow,
) => {
  if (!state.currentWorkflow) return;
  state.currentWorkflow = fn(state.currentWorkflow);
  addEditedField(state, field);
};

// ── Slice ──────────────────────────────────────────────────────────

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    initializeWorkflow: (state) => {
      state.currentWorkflow = createDefaultWorkflow();
      state.isEdited = false;
      state.editedFields = [];
    },

    clearWorkflow: (state) => {
      state.currentWorkflow = null;
      state.selectedWorkflowId = null;
      state.isEdited = false;
      state.editedFields = [];
    },

    setSelectedWorkflowId: (state, action: PayloadAction<string | null>) => {
      state.selectedWorkflowId = action.payload;
    },

    // ── Basic Info ───────────────────────────────────────────────

    updateWorkflowBasicInfo: (state, action: PayloadAction<{ name?: string; description?: string; trigger?: string; isActive?: boolean }>) => {
      withWorkflow(state, Object.keys(action.payload).join(','), w => wf.updateBasicInfo(w, action.payload));
    },

    setConditionOperator: (state, action: PayloadAction<ConditionOperator>) => {
      withWorkflow(state, 'conditionOperator', w => wf.setConditionOperator(w, action.payload));
    },

    // ── Conditions ───────────────────────────────────────────────

    addCondition: (state, action: PayloadAction<WorkflowCondition>) => {
      withWorkflow(state, 'conditions', w => wf.addCondition(w, action.payload));
    },

    updateCondition: (state, action: PayloadAction<{ index: number; field: keyof WorkflowCondition; value: string }>) => {
      const { index, field, value } = action.payload;
      withWorkflow(state, 'conditions', w => wf.updateConditionField(w, index, field, value));
    },

    removeCondition: (state, action: PayloadAction<number>) => {
      withWorkflow(state, 'conditions', w => wf.removeCondition(w, action.payload));
    },

    setConditions: (state, action: PayloadAction<WorkflowCondition[]>) => {
      withWorkflow(state, 'conditions', w => wf.setConditions(w, action.payload));
    },

    // ── Steps ────────────────────────────────────────────────────

    addStep: (state, action: PayloadAction<WorkflowStep>) => {
      withWorkflow(state, 'steps', w => wf.addStep(w, action.payload));
    },

    updateStep: (state, action: PayloadAction<{ index: number; updates: Partial<WorkflowStep> }>) => {
      const { index, updates } = action.payload;
      withWorkflow(state, 'steps', w => wf.mergeStep(w, index, updates));
    },

    removeStep: (state, action: PayloadAction<number>) => {
      withWorkflow(state, 'steps', w => wf.removeStep(w, action.payload));
    },

    setSteps: (state, action: PayloadAction<WorkflowStep[]>) => {
      withWorkflow(state, 'steps', w => wf.setSteps(w, action.payload));
    },

    reorderSteps: (state, action: PayloadAction<WorkflowStep[]>) => {
      withWorkflow(state, 'steps', w => wf.reorderSteps(w, action.payload));
    },

    // ── Lifecycle ────────────────────────────────────────────────

    markAsSaved: (state) => {
      state.isEdited = false;
      state.editedFields = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(workflowQueriesApi.endpoints.getWorkflow.matchFulfilled, (state, action) => {
        state.currentWorkflow = action.payload;
        state.selectedWorkflowId = action.payload.id;
        state.isEdited = false;
        state.editedFields = [];
      })
      .addMatcher(workflowMutationsApi.endpoints.createWorkflow.matchFulfilled, (state, action) => {
        state.currentWorkflow = action.payload;
        if (action.payload.id) state.selectedWorkflowId = action.payload.id;
        state.isEdited = false;
        state.editedFields = [];
      })
      .addMatcher(workflowMutationsApi.endpoints.deleteWorkflow.matchFulfilled, (state, action) => {
        const deletedId = action.meta.arg.originalArgs.id;
        if (state.currentWorkflow?.id === deletedId) {
          state.currentWorkflow = null;
          state.selectedWorkflowId = null;
        }
        state.isEdited = false;
        state.editedFields = [];
      });
  },
});

export const {
  initializeWorkflow,
  clearWorkflow,
  setSelectedWorkflowId,
  updateWorkflowBasicInfo,
  setConditionOperator,
  addCondition,
  updateCondition,
  removeCondition,
  setConditions,
  addStep,
  updateStep,
  removeStep,
  setSteps,
  reorderSteps,
  markAsSaved,
} = workflowSlice.actions;

export default workflowSlice.reducer;
